<template>
	<AppLayout
		:title="trans('enrollments.create.title')"
		:subtitle="trans('enrollments.create.subtitle')"
		:return-url="route('events.show', event.id)"
		:return-label="event.title"
	>
		<p class="text-gray-400 text-sm">{{ trans('enrollments.create.help') }}</p>
		<Alert v-if="!registrationStage.refundable" :color="Color.Blue">{{ trans('enrollments.not_refundable') }}</Alert>
		<div class="grid grid-cols-1 md:grid-cols-9 gap-4">
			<div class="md:col-span-5 lg:col-span-6 flex flex-col gap-4">
				<SelectPack v-for="pack in packs" :key="`pack-${pack.id}`" :pack="pack" :selected-packs="selectedPacks" @selected="select"/>
				<LabeledObject v-if="attachmentStage" required>
					<template #label>{{ trans('events.stages.attachment.essay') }}</template>
					<input type="file" accept="application/pdf" @change="loadFile($event)">
				</LabeledObject>
			</div>
			<div class="md:col-span-4 lg:col-span-3">
				<Card class="sticky top-16" :title="trans('enrollments.summary.title')">
					<div v-if="registrationStage.price > 0" class="flex">
						<p class="grow">{{ trans('events.stages.registration.title') }}</p>
						<p>$ {{ formatNumber(registrationStage.price, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</p>
					</div>
					<div v-for="pack in selectedPacks" class="flex">
						<p class="grow">{{ pack.title }}</p>
						<p>$ {{ formatNumber(pack.price, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</p>
					</div>
					<hr>
					<div class="flex font-bold">
						<p class="grow">{{ trans('enrollments.invoice.total') }}</p>
						<p>$ {{ formatNumber(total, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</p>
					</div>
					<p v-if="!canProceed" class="text-gray-400">{{ trans('enrollments.cannot_proceed') }}</p>
					<PrimaryButton :color="Color.Green" data-testid="proceed-button" :disabled="!canProceed" @click.stop="proceed">
						<i class="fa-solid fa-check mr-2"/>
						{{ trans('general.proceed') }}
					</PrimaryButton>
				</Card>
			</div>
		</div>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {router} from '@inertiajs/vue3'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Alert from '@/Components/Alerts/Alert.vue'
import {type Pack} from '@/Types/Events/Packs/Pack'
import LabeledObject from '@/Components/LabeledObject.vue'
import {computed, type ComputedRef, ref, type Ref} from 'vue'
import {type Event as AArEIIEvent} from '@/Types/Events/Event'
import SelectPack from '@/Components/Enrollments/SelectPack.vue'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import {type AttachmentStage} from '@/Types/Events/Stages/AttachmentStage'
import {type RegistrationStage} from '@/Types/Events/Stages/RegistrationStage'
import {formatNumber, showInfo, showSuccess, showValidationErrors, showWarning} from '@/helpers'

interface Attachment {
	file:Blob|null,
}

const props = defineProps<{
	event:AArEIIEvent,
	packs:Array<Pack>,
	selectedPacks:Array<Pack>,
	attachmentStage?:AttachmentStage,
	registrationStage:RegistrationStage,
}>()

const sending:Ref<boolean> = ref(false)
const event:Ref<AArEIIEvent> = ref(props.event)
const packs:Ref<Array<Pack>> = ref(props.packs)
const selectedPacks:Ref<Array<Pack>> = ref(props.selectedPacks)
const registrationStage:Ref<RegistrationStage> = ref(props.registrationStage)
const attachmentStage:Ref<AttachmentStage|undefined> = ref(props.attachmentStage)
const attachment:Ref<Attachment> = ref({
	file: null,
})

const subtotal:ComputedRef<number> = computed(() => {
	let subtotal = props.registrationStage.price
	for(let i = 0; i < selectedPacks.value.length; i++){
		subtotal += selectedPacks.value[i].price
	}
	return subtotal
})
const total:ComputedRef<number> = computed(() => {
	return subtotal.value
})
const canProceed:ComputedRef<boolean> = computed(() => {
	if(registrationStage.value.selection_mandatory){
		return selectedPacks.value.filter((p:Pack) => !p.mandatory).length > 0
	}
	return true
})

function loadFile(event: Event):void{
	if(event.target && event.target instanceof HTMLInputElement && event.target.files){
		attachment.value.file = event.target.files[0]
	}
}

function select(pack:Pack):void{
	if(props.registrationStage.selection_type === 'single'){
		if(!pack.mandatory){
			let previousSelectedIndex = -1
			let previousSelectedId = null
			if(selectedPacks.value.length > 0){
				previousSelectedIndex = selectedPacks.value.findIndex(selectedPack => !selectedPack.mandatory)
				if(previousSelectedIndex > -1){
					previousSelectedId = selectedPacks.value[previousSelectedIndex].id
					selectedPacks.value.splice(previousSelectedIndex, 1)
				}
			}
			if(props.registrationStage.selection_mandatory || previousSelectedId !== pack.id){
				selectedPacks.value.push(pack)
			}
		}
	} else {
		toggle(pack)
	}
}

function toggle(pack:Pack):void{
	const index = selectedPacks.value.findIndex((p) => p.id === pack.id)
	if(index === -1){
		selectedPacks.value.push(pack)
	} else {
		if(!selectedPacks.value[index].mandatory){
			selectedPacks.value.splice(index, 1)
		}
	}
}

function proceed():void{
	if(!sending.value && canProceed.value){
		if(props.attachmentStage !== null && attachment.value.file === null){
			showWarning(trans('events.stages.attachment.required'))
		} else {
			showInfo(trans('enrollments.saving'))
			sending.value = true
			const formData = new FormData
			formData.append('packs', JSON.stringify(selectedPacks.value.map((pack) => pack.id)))
			formData.append('registration_stage_id', registrationStage.value.id.toString())
			if(props.attachmentStage){
				formData.append('attachment', attachment.value.file ?? '')
				formData.append('attachment_stage_id', attachmentStage.value?.id.toString() ?? '')
			}
			axios.post(route('events.enrollments.store', event.value.id), formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				}
			}).then(({data}) => {
				showSuccess(trans('general.inserted'))
				router.get(route('events.enrollments.show', data.enrollment_id))
			}).catch(showValidationErrors).finally(() => {
				sending.value = false
			})
		}
	}
}
</script>
